import React, { useState, useEffect } from "react";
import { Country, State } from "country-state-city";
import {
  ADDRESS,
  PASSWORD,
  PROFILE_DATA,
} from "components/constants/profileFields";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
  errorClean,
  messageClean,
  updateUser,
  updateUserPassword,
} from "redux-store/user/user.slice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "redux-store/store";
import {
  successMessage,
  user,
  userActivityInProcess,
} from "redux-store/user/user.selectors";
import {
  validateAddress,
  validateCity,
  validateConfirmNewPassword,
  validateEmail,
  validateFullName,
  validateName,
  validatePassword,
  validatePhone,
  validateZipCode,
} from "components/utils/regexValidation";

const UserEditProfile = () => {
  const [states, setStates] = useState<any | null>([]);
  const [countryCode, setCountryCode] = useState<any | null>(null);

  const { userId } = useParams();

  const [errMessages, setMessages] = useState({
    fullName: "",
    firstName: "",
    lastName: "",
    zipCode: "",
    phone: "",
    address: "",
    address2: "",
    city: "",
    email: "",
    password: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const dispatch = useDispatch<AppDispatch>();
  const userData = useSelector(user);
  const loading = useSelector(userActivityInProcess);
  const succesMessage = useSelector(successMessage);
  const editMessage = useSelector(successMessage);

  if (succesMessage === "Info updated successfully") {
    setTimeout(() => {
      dispatch(messageClean());
    }, 5000);
  }

  const [passwordFormData, setPasswordFormData] = useState({
    password: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [formData, setFormData] = useState({
    _id: userData?._id,
    fullName: userData?.fullName,
    email: userData?.email,
    password: userData?.password,
    firstName: userData?.firstName,
    lastName: userData?.lastName,
    phone: userData?.phone,
    address: userData?.address,
    address2: userData?.address2,
    city: userData?.city,
    country: userData?.country,
    state: userData?.state,
    zipCode: userData?.zipCode,
  });

  const countryData = Country.getAllCountries().map((city) => ({
    value: city.name,
    code: city.isoCode,
  }));

  const stateData = (countryCode: any) => {
    let newCities = State.getStatesOfCountry(countryCode).map((state) => ({
      value: state.name,
    }));
    setStates(newCities);
  };

  const changePasswordHandler = (event: any) => {
    const value = event.target.value;
    setPasswordFormData({ ...passwordFormData, [event.target.name]: value });
  };

  const onChange = (event: any) => {
    const value = event.target.value;
    setFormData({ ...formData, [event.target.name]: value });
  };

  function updatePasswordHandler() {
    const { password, newPassword, confirmNewPassword } = passwordFormData;
    let [validPass, validNewPass, validConfirmNewPass] = [true, true, true];

    if (!validatePassword(password)) {
      validPass = false;
    }
    if (!validatePassword(newPassword)) {
      validNewPass = false;
    }
    if (!validateConfirmNewPassword(newPassword, confirmNewPassword)) {
      validConfirmNewPass = false;
    }

    setMessages({
      ...errMessages,
      password: validPass
        ? ""
        : "Password must have at least 8 digits length, 1 character, 1 number",
      newPassword: validNewPass
        ? ""
        : "Password must have at least 8 digits length, 1 character, 1 number",
      confirmNewPassword: validConfirmNewPass ? "" : "Password didn't match",
    });

    if (validPass && validNewPass && validConfirmNewPass) {
      dispatch(errorClean());
      if (editMessage !== "") {
        dispatch(messageClean());
      }
      const editPassword = new FormData();
      editPassword.append("password", passwordFormData.password);
      editPassword.append("newPassword", passwordFormData.newPassword);
      editPassword.append(
        "confirmNewPassword",
        passwordFormData.confirmNewPassword
      );
      dispatch(updateUserPassword(editPassword));
    }
  }

  async function updateUserDataHandler(e: any) {
    e.preventDefault();
    let {
      fullName,
      firstName,
      lastName,
      email,
      address,
      address2,
      zipCode,
      phone,
      city,
    } = formData;

    let [
      validEmail,
      validPass,
      validFullName,
      validFirstName,
      validLastName,
      validPhone,
      validZip,
      validAddress,
      validAddress2,
      validCity,
    ] = [true, true, true, true, true, true, true, true, true, true];

    if (!validateEmail(email)) {
      validEmail = false;
    }
    if (!validateAddress(address)) {
      validAddress = false;
    }
    if (!validateAddress(address2)) {
      validAddress2 = false;
    }
    if (!validateFullName(fullName)) {
      validFullName = false;
    }
    if (!validateName(firstName)) {
      validFirstName = false;
    }
    if (!validateName(lastName)) {
      validLastName = false;
    }
    if (zipCode !== null && !validateZipCode(zipCode)) {
      validZip = false;
    }
    if (phone !== null && !validatePhone(phone)) {
      validPhone = false;
    }
    if (!validateCity(city)) {
      validCity = false;
    }
    setMessages({
      ...errMessages,
      email: validEmail ? "" : "Invalid Email",
      password: validPass
        ? ""
        : "Password must have at least 8 digits length, 1 character, 1 number",
      fullName: validFullName
        ? ""
        : "Full name need to be atleast 3 characters",
      firstName: validFirstName ? "" : "Name is invalid",
      lastName: validLastName ? "" : "Name is invalid",
      zipCode: validZip ? "" : "Zip code need to be more than 3",
      phone: validPhone ? "" : "Phone no need to be more than 5",
      address: validAddress ? "" : "Address is invalid",
      address2: validAddress2 ? "" : "Address is invalid",
      city: validCity ? "" : "City is invalid",
    });
    if (
      validEmail &&
      validPass &&
      validFullName &&
      validFirstName &&
      validLastName &&
      validPhone &&
      validZip &&
      validAddress &&
      validAddress2 &&
      validCity
    ) {
      dispatch(errorClean());
      if (succesMessage !== "") {
        dispatch(messageClean());
      }

      // convert null values into string because regex don't work with null values
      const editUser = new FormData();
      editUser.append("_id", formData?._id);
      editUser.append("fullName", formData?.fullName);
      editUser.append("email", formData?.email);
      editUser.append("firstName", formData?.firstName);
      editUser.append("lastName", formData?.lastName);
      phone !== null && editUser.append("phone", formData?.phone);
      editUser.append("address", formData?.address);
      editUser.append("address2", formData?.address2);
      editUser.append("city", formData?.city);
      editUser.append("country", formData?.country);
      editUser.append("state", formData?.state);
      zipCode !== null && editUser.append("zipCode", formData?.zipCode);

      await dispatch(updateUser(editUser));
    }
  }

  useEffect(() => {
    stateData(countryCode);
    setFormData({
      ...formData,
      state: State?.getStatesOfCountry(countryCode)[0]?.name,
    });
  }, [countryCode]);

  useEffect(() => {
    dispatch(errorClean());
    dispatch(messageClean());
  }, []);

  useEffect(() => {
    userData &&
      setFormData({
        _id: userData?._id,
        fullName: userData?.fullName,
        email: userData?.email,
        password: userData?.password,
        firstName: userData?.firstName,
        lastName: userData?.lastName,
        phone: userData?.phone,
        address: userData?.address,
        address2: userData?.address2,
        city: userData?.city,
        country: userData?.country,
        state: userData?.state,
        zipCode: userData?.zipCode,
      });
    userData && stateData(userData?.country);
  }, [userData]);

  return (
    <React.Fragment>
      <div className="tw-flex tw-justify-center">
        <div className="tw-w-[90%] tw-py-3 tw-bg-[#FFFFFF] tw-rounded-xl tw-shadow-3xl">
          <div className="tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-px-5 lg:tw-px-10">
            <div className=" tw-text-black tw-flex tw-items-center">
              <div className="tw-text-[20px] md:tw-text-[26px] lg:tw-text-3xl tw-font-bold">
                Edit Profile
              </div>
            </div>
            <div>
              <Link
                to={`/${
                  userData?.isAdmin ? "admin" : "user"
                }/my-profile/${userId}`}
              >
                <button className="tw-text-white tw-text-[16px] md:tw-text-[20px] tw-mt-5 lg:tw-mt-0 tw-ml-0 lg:tw-ml-5 tw-border-2 tw-px-[40px] tw-py-2 lg:tw-py-3 tw-border-reddishOrange tw-bg-reddishOrange tw-rounded-[10px] hover:tw-text-white hover:tw-bg-darkRed hover:tw-border-darkRed  tw-cursor-pointer">
                  Back
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-flex tw-justify-center">
        <div className="tw-mt-3 tw-w-[90%] tw-py-8 lg:tw-py-[98px]  tw-px-[5%] lg:tw-px-[12%] xl:tw-px-[18%] tw-bg-[#FFFFFF] tw-rounded-xl tw-shadow-3xl">
          <div className="tw-flex tw-flex-col lg:tw-flex-row tw-justify-between">
            <div className="tw-flex tw-flex-col tw-justify-between">
              {PROFILE_DATA.map((value, i) => {
                return (
                  <div className="tw-pt-5 tw-w-full md:tw-w-auto">
                    <div className="did-floating-label-content sm:tw-w-96 lg:tw-w-80 ">
                      <input
                        className="did-floating-select"
                        style={{ backgroundImage: "none" }}
                        onChange={onChange}
                        name={value?.name}
                        value={
                          (formData as any)[value?.name] &&
                          (formData as any)[value?.name]
                        }
                      />
                      <label className="did-floating-label">
                        {value?.label}
                      </label>
                    </div>
                    <span className="tw-text-xs tw-text-red-500">
                      {(errMessages as any)[value?.name]}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="tw-text-black tw-flex tw-flex-col tw-justify-between tw-pt-7 lg:tw-pt-0">
              <div className="tw-text-[20px] md:tw-text-[26px] lg:tw-text-3xl tw-font-bold ">
                Change Password
                <div className="lg:tw-h-[4px] tw-w-[170px] lg:tw-w-[200px] tw-mt-4 tw-border-4 tw-rounded-sm tw-border-reddishOrange"></div>
              </div>
              <div className="tw-flex tw-flex-col">
                {PASSWORD.map((value) => {
                  return (
                    <div className="tw-pt-5 tw-w-full md:tw-w-auto">
                      <div className="did-floating-label-content sm:tw-w-96 lg:tw-w-80 ">
                        <input
                          className="did-floating-select"
                          style={{ backgroundImage: "none" }}
                          onChange={changePasswordHandler}
                          name={value?.name}
                          type="password"
                          value={(passwordFormData as any)[value?.name]}
                        />
                        <label className="did-floating-label">
                          {value?.label}
                        </label>
                      </div>
                      <span className="tw-text-xs tw-text-red-500">
                        {(errMessages as any)[value?.name]}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="tw-pt-2 lg:tw-pt-10 tw-flex tw-gap-4 tw-flex-col">
                <span className="tw-text-xs tw-text-green-500 tw-px-[35px]">
                  {editMessage}
                </span>
                <button
                  onClick={updatePasswordHandler}
                  className="sm:tw-w-96 lg:tw-w-full tw-text-white tw-text-[16px] md:tw-text-[20px] tw-border-2 tw-px-[40px] tw-py-3 tw-border-reddishOrange tw-bg-reddishOrange hover:tw-text-white hover:tw-bg-darkRed hover:tw-border-darkRed tw-rounded-[10px] tw-cursor-pointer"
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-justify-center tw-pt-10">
            <div className="tw-flex tw-flex-wrap tw-justify-between">
              <div className="tw-text-[20px] md:tw-text-[26px] lg:tw-text-3xl tw-font-bold ">
                Address
                <div className="tw-h-[4px]  tw-w-[170px] lg:tw-w-[200px] tw-mt-4 tw-border-4 tw-rounded-sm tw-border-reddishOrange"></div>
              </div>
              <div>
                <div className="tw-flex tw-flex-wrap tw-justify-between">
                  {ADDRESS.map((value) => {
                    return (
                      <div className="tw-pt-5 tw-w-full md:tw-w-auto">
                        <div className="did-floating-label-content sm:tw-w-96 lg:tw-w-80 ">
                          <input
                            className="did-floating-select"
                            style={{ backgroundImage: "none" }}
                            onChange={onChange}
                            name={value?.name}
                            value={(formData as any)[value?.name]}
                          />
                          <label className="did-floating-label">
                            {value?.label}
                          </label>
                        </div>
                        <span className="tw-text-xs tw-text-red-500">
                          {(errMessages as any)[value?.name]}
                        </span>
                      </div>
                    );
                  })}
                  {/* Dropdown */}
                  <div className="tw-pt-5 tw-w-full md:tw-w-auto">
                    <div className="did-floating-label-content sm:tw-w-96 lg:tw-w-80 ">
                      <select
                        className="did-floating-select"
                        name="country"
                        onChange={(e) => {
                          setCountryCode(e.target.value);
                          onChange(e);
                        }}
                        value={
                          (formData as any)["country"] &&
                          (formData as any)["country"]
                        }
                      >
                        <option value="">Select Country</option>
                        {countryData.map((option, index) => {
                          return (
                            <option
                              key={index}
                              value={option.code}
                              selected={
                                option.code === (formData as any)["country"] &&
                                (formData as any)["country"]
                                  ? true
                                  : false
                              }
                            >
                              {option.value}
                            </option>
                          );
                        })}
                      </select>

                      <label className="did-floating-label">Country</label>
                    </div>
                  </div>
                  <div className="tw-pt-5 tw-w-full md:tw-w-auto">
                    <div className="did-floating-label-content sm:tw-w-96 lg:tw-w-80 ">
                      <select
                        className="did-floating-select"
                        name="state"
                        onChange={onChange}
                        value={
                          (formData as any)["state"] &&
                          (formData as any)["state"]
                        }
                      >
                        {states?.map((option: any, index: any) => {
                          return (
                            <option
                              key={index}
                              value={option.value}
                              selected={
                                option.value === (formData as any)["state"] &&
                                (formData as any)["state"]
                                  ? true
                                  : false
                              }
                            >
                              {option.value}
                            </option>
                          );
                        })}
                      </select>
                      <label className="did-floating-label">State</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tw-pt-4 lg:tw-pt-7 tw-flex tw-flex-col min-[375px]:tw-flex-row">
            <button
              onClick={updateUserDataHandler}
              className="tw-text-white tw-text-[16px] md:tw-text-[20px] tw-border-2 tw-px-[36px] sm:tw-px-[40px] tw-py-2 lg:tw-py-3 tw-rounded-[10px] tw-border-reddishOrange tw-bg-reddishOrange hover:tw-text-white hover:tw-bg-darkRed hover:tw-border-darkRed tw-cursor-pointer"
            >
              {loading ? "Saving ..." : " Save"}
            </button>
            <Link to={`/user/my-profile/${userId}`}>
              <button className="tw-border-2 tw-w-full min-[375px]:tw-w-auto tw-text-[16px] md:tw-text-[20px] tw-rounded-[10px] tw-mt-5 min-[375px]:tw-mt-0 min-[375px]:tw-ml-5 tw-px-[36px] sm:tw-px-[40px] tw-py-2 lg:tw-py-3 tw-border-black tw-text-black hover:tw-bg-slate-200 hover:tw-border-slate-200 hover:tw-text-black cursor-pointer">
                Cancel
              </button>
            </Link>
          </div>
          {succesMessage === "Info updated successfully" && (
            <p className="tw-text-green-400 tw-text-lg tw-pl-2 tw-pt-3">
              Editing Successful!
            </p>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default UserEditProfile;
